// src/contexts/AuthStatusContext.tsx
import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { CLIENT_ID } from '../../config/env';
import { setGlobalAuthInstance } from '../../utils/authUtils';

export type AuthStatus = 'loading' | 'unauth' | 'authorized';
export type GroupStatus = 'loading' | 'unauthorized' | 'applicant';

interface AuthState {
    authStatus: AuthStatus;
    groupStatus: GroupStatus;
    profile?: Record<string, unknown>;
    logout: () => Promise<void>;
}

const AuthStatusContext = createContext<AuthState | undefined>(undefined);

export const useAuthStatus = (): AuthState => {
    const ctx = useContext(AuthStatusContext);
    if (!ctx) throw new Error('useAuthStatus must be used inside <AuthStatusProvider>');
    return ctx;
};

export const AuthStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useAuth();              // from react-oidc-context

    // Set the global auth instance for ApiClient to use for token refresh
    useEffect(() => {
        setGlobalAuthInstance(auth);
    }, [auth]);

    const logout = async (): Promise<void> => {
        try {
            const clientId = CLIENT_ID;
            const signOutUri = `${window.location.origin}/signout`;

            if (auth.signoutRedirect && clientId) {
                await auth.signoutRedirect({
                    post_logout_redirect_uri: signOutUri,
                    extraQueryParams: {
                        client_id: clientId,
                        logout_uri: signOutUri
                    },
                });
                return;
            }
        } catch (err) {
            console.error("signoutRedirect failed", err);
            // Even if signout fails, clear storage and redirect to signout page
            try {
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = '/signout';
            } catch (e) {
                console.error("Error during cleanup:", e);
            }
        }
    }
    const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
    const [groupStatus, setGroupStatus] = useState<GroupStatus>('loading');
    const [profile, setProfile] = useState<Record<string, unknown>>();

    useEffect(() => {
        if (auth.isLoading) {
            setAuthStatus('loading');
            setGroupStatus('loading');
            return;
        }
        if (!auth.isAuthenticated) {
            setAuthStatus('unauth');
            setGroupStatus('loading');
            setProfile(undefined);
            return;
        }
        // authenticated
        setAuthStatus('authorized');
        setProfile(auth.user?.profile);
        setGroupStatus('applicant');
    }, [auth]);

    return (
        <AuthStatusContext.Provider value={{ authStatus, groupStatus, profile, logout }}>
            {children}
        </AuthStatusContext.Provider>
    );
};